<script setup lang="ts">
type SortOption = 'createdAt' | 'eventTime';
type SortDir = 'desc' | 'asc';

defineProps<{
  sortOption: SortOption;
  sortDir: SortDir;
}>();

const emit = defineEmits<{
  select: [option: SortOption];
}>();

const options: { key: SortOption; label: string }[] = [
  { key: 'eventTime', label: 'Event Time' },
  { key: 'createdAt', label: 'Created' }
];
</script>

<template>
  <div class="sort-bar"
    role="group"
    aria-label="Sort notes by">
    <button v-for="opt in options"
      :key="opt.key"
      type="button"
      class="sort-btn"
      :class="{ active: sortOption === opt.key }"
      :aria-pressed="sortOption === opt.key"
      :aria-label="`Sort by ${opt.label}${sortOption === opt.key ? `, ${sortDir === 'desc' ? 'descending' : 'ascending'}` : ''}`"
      @click="emit('select', opt.key)">
      {{ opt.label }}
      <FontAwesomeIcon v-if="sortOption === opt.key"
        :icon="['fas', sortDir === 'desc' ? 'arrow-down' : 'arrow-up']"
        class="sort-dir-icon"
        aria-hidden="true"/>
    </button>
  </div>
</template>

<style scoped lang="scss">
.sort-bar {
  display: flex;
  margin: 8px 0 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.sort-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 4px;
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
  background: transparent;
  border: none;
  border-right: 1px solid var(--color-border);
  cursor: pointer;
  color: rgba(0, 0, 0, 0.55);
  transition:
    background 0.15s ease,
    color 0.15s ease;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  &.active {
    background: var(--p-primary-color, #0066ff);
    color: #fff;
    font-weight: 600;
  }

  &:not(.active):hover {
    background: var(--color-background-mute, #f5f5f5);
    color: var(--color-text);
  }
}

.sort-dir-icon {
  font-size: 9px;
  opacity: 0.85;
}
</style>
